import { compensationApiBaseUrl, employeesApiBaseUrl, payrollCalculationApiBaseUrl, payrollConfigApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { HealthcareRatesGateway } from './HealthcareRatesGateway';
import type {
  AllowedWorkTypes,
  CompanyIndustry,
  CompanyIndustryProfile,
  CompensationSetupReview,
  IndustryChangePreview,
  CreateDisciplineAssignmentInput,
  CreateServiceRateInput,
  Discipline,
  DisciplineAssignment,
  DisciplineCapabilities,
  DisciplineInput,
  EmployeeRateCapabilities,
  ListDisciplinesParams,
  ListServiceRatesParams,
  ListServiceTypesParams,
  NaicsIndustrySearch,
  NaicsSector,
  ServiceRate,
  ServiceType,
  ServiceTypeCapabilities,
  ServiceTypeInput,
  SetIndustryProfileInput,
} from './types';
import {
  allowedWorkTypesFromWire,
  allowedWorkTypesToWire,
  companyIndustryFromWire,
  companyIndustryProfileFromWire,
  compensationSetupReviewFromWire,
  industryChangePreviewFromWire,
  industryProfileToWire,
  naicsSearchFromWire,
  naicsSectorsFromWire,
  createAssignmentToWire,
  createServiceRateToWire,
  disciplineAssignmentFromWire,
  disciplineAssignmentsFromWire,
  disciplineCapabilitiesFromWire,
  disciplineFromWire,
  disciplinesFromWire,
  disciplineToWire,
  employeeRateCapabilitiesFromWire,
  serviceRateFromWire,
  serviceRatesFromWire,
  serviceTypeCapabilitiesFromWire,
  serviceTypeFromWire,
  serviceTypesFromWire,
  serviceTypeToWire,
  supportedPayBasesFromWire,
} from './wireMapping';

function query(params: Record<string, string | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    q.set(key, String(value));
  }
  const text = q.toString();
  return text ? `?${text}` : '';
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

/** The real services. Paths and bodies are those of contract section 10; the wire is read and written only through wireMapping.ts. */
export class HttpHealthcareRatesGateway implements HealthcareRatesGateway {
  readonly isSynthetic = false;
  private readonly employees = createJsonClient(employeesApiBaseUrl);
  private readonly payrollConfig = createJsonClient(payrollConfigApiBaseUrl);
  private readonly compensation = createJsonClient(compensationApiBaseUrl);
  private readonly payrollCalculation = createJsonClient(payrollCalculationApiBaseUrl);

  // ---- disciplines ----
  async getDisciplineCapabilities(signal?: AbortSignal): Promise<DisciplineCapabilities> {
    return disciplineCapabilitiesFromWire(await this.employees('/api/discipline-capabilities', undefined, signal));
  }

  async listDisciplines(params?: ListDisciplinesParams, signal?: AbortSignal): Promise<Discipline[]> {
    const qs = query({ industry: params?.industry, search: params?.search, includeInactive: params?.includeInactive });
    return disciplinesFromWire(await this.employees(`/api/disciplines${qs}`, undefined, signal));
  }

  async createDiscipline(input: DisciplineInput): Promise<Discipline> {
    return disciplineFromWire(await this.employees('/api/disciplines', post(disciplineToWire(input))));
  }

  async updateDiscipline(id: string, input: DisciplineInput, expectedConcurrencyVersion: number): Promise<Discipline> {
    return disciplineFromWire(
      await this.employees(`/api/disciplines/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(disciplineToWire(input, expectedConcurrencyVersion)) }),
    );
  }

  async deactivateDiscipline(id: string, expectedConcurrencyVersion: number): Promise<Discipline> {
    return disciplineFromWire(await this.employees(`/api/disciplines/${encodeURIComponent(id)}/deactivate`, post({ expectedConcurrencyVersion })));
  }

  async reactivateDiscipline(id: string, expectedConcurrencyVersion: number): Promise<Discipline> {
    return disciplineFromWire(await this.employees(`/api/disciplines/${encodeURIComponent(id)}/reactivate`, post({ expectedConcurrencyVersion })));
  }

  // ---- assignments ----
  async listDisciplineAssignments(employeeId: string, signal?: AbortSignal): Promise<DisciplineAssignment[]> {
    return disciplineAssignmentsFromWire(await this.employees(`/api/employees/${encodeURIComponent(employeeId)}/discipline-assignments`, undefined, signal));
  }

  async createDisciplineAssignment(employeeId: string, input: CreateDisciplineAssignmentInput): Promise<DisciplineAssignment> {
    return disciplineAssignmentFromWire(
      await this.employees(`/api/employees/${encodeURIComponent(employeeId)}/discipline-assignments`, post(createAssignmentToWire(input))),
    );
  }

  async endDisciplineAssignment(employeeId: string, assignmentId: string, effectiveEndDate: string): Promise<DisciplineAssignment> {
    return disciplineAssignmentFromWire(
      await this.employees(
        `/api/employees/${encodeURIComponent(employeeId)}/discipline-assignments/${encodeURIComponent(assignmentId)}/end`, post({ effectiveEndDate }),
      ),
    );
  }

  async listActiveDisciplineAssignments(employeeId: string, asOf: string, signal?: AbortSignal): Promise<DisciplineAssignment[]> {
    return disciplineAssignmentsFromWire(
      await this.employees(`/api/employees/${encodeURIComponent(employeeId)}/discipline-assignments/active${query({ asOf })}`, undefined, signal),
    );
  }

  // ---- allowed work types ----
  async getAllowedWorkTypes(employeeId: string, signal?: AbortSignal): Promise<AllowedWorkTypes> {
    return allowedWorkTypesFromWire(await this.employees(`/api/employees/${encodeURIComponent(employeeId)}/allowed-service-types`, undefined, signal));
  }

  async setAllowedWorkTypes(employeeId: string, serviceTypeIds: readonly string[]): Promise<AllowedWorkTypes> {
    return allowedWorkTypesFromWire(
      await this.employees(
        `/api/employees/${encodeURIComponent(employeeId)}/allowed-service-types`, { method: 'PUT', body: JSON.stringify(allowedWorkTypesToWire(serviceTypeIds)) },
      ),
    );
  }

  // ---- service types ----
  async getServiceTypeCapabilities(signal?: AbortSignal): Promise<ServiceTypeCapabilities> {
    return serviceTypeCapabilitiesFromWire(await this.payrollConfig('/api/service-type-capabilities', undefined, signal));
  }

  async listServiceTypes(params?: ListServiceTypesParams, signal?: AbortSignal): Promise<ServiceType[]> {
    const qs = query({ industry: params?.industry, search: params?.search, includeInactive: params?.includeInactive });
    return serviceTypesFromWire(await this.payrollConfig(`/api/service-types${qs}`, undefined, signal));
  }

  async createServiceType(input: ServiceTypeInput): Promise<ServiceType> {
    return serviceTypeFromWire(await this.payrollConfig('/api/service-types', post(serviceTypeToWire(input))));
  }

  async updateServiceType(id: string, input: ServiceTypeInput, expectedConcurrencyVersion: number): Promise<ServiceType> {
    return serviceTypeFromWire(
      await this.payrollConfig(`/api/service-types/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(serviceTypeToWire(input, expectedConcurrencyVersion)) }),
    );
  }

  async deactivateServiceType(id: string, expectedConcurrencyVersion: number): Promise<ServiceType> {
    return serviceTypeFromWire(await this.payrollConfig(`/api/service-types/${encodeURIComponent(id)}/deactivate`, post({ expectedConcurrencyVersion })));
  }

  async reactivateServiceType(id: string, expectedConcurrencyVersion: number): Promise<ServiceType> {
    return serviceTypeFromWire(await this.payrollConfig(`/api/service-types/${encodeURIComponent(id)}/reactivate`, post({ expectedConcurrencyVersion })));
  }

  // ---- industry ----
  async getCompanyIndustry(signal?: AbortSignal): Promise<CompanyIndustry> {
    return companyIndustryFromWire(await this.payrollConfig('/api/company-industry', undefined, signal));
  }

  async setCompanyIndustry(industry: string): Promise<CompanyIndustry> {
    return companyIndustryFromWire(await this.payrollConfig('/api/company-industry', { method: 'PUT', body: JSON.stringify({ industry }) }));
  }

  // ---- industry profile and the NAICS reference ----
  async getIndustryProfile(signal?: AbortSignal): Promise<CompanyIndustryProfile> {
    return companyIndustryProfileFromWire(await this.payrollConfig('/api/company-industry-profile', undefined, signal));
  }

  async setIndustryProfile(input: SetIndustryProfileInput): Promise<CompanyIndustryProfile> {
    return companyIndustryProfileFromWire(await this.payrollConfig('/api/company-industry-profile', { method: 'PUT', body: JSON.stringify(industryProfileToWire(input)) }));
  }

  async listNaicsSectors(signal?: AbortSignal): Promise<NaicsSector[]> {
    return naicsSectorsFromWire(await this.payrollConfig('/api/reference/naics/sectors', undefined, signal)).sectors;
  }

  async getCompensationSetupReview(signal?: AbortSignal): Promise<CompensationSetupReview> {
    return compensationSetupReviewFromWire(await this.payrollConfig('/api/compensation-setup-review', undefined, signal));
  }

  async previewIndustryChange(input: SetIndustryProfileInput, signal?: AbortSignal): Promise<IndustryChangePreview> {
    return industryChangePreviewFromWire(await this.payrollConfig('/api/company-industry-profile/preview', post(industryProfileToWire(input)), signal));
  }

  async searchNaicsIndustries(params: { sector?: string | undefined; q?: string | undefined; limit?: number | undefined }, signal?: AbortSignal): Promise<NaicsIndustrySearch> {
    const qs = query({ sector: params.sector, q: params.q, limit: params.limit === undefined ? undefined : String(params.limit) });
    return naicsSearchFromWire(await this.payrollConfig(`/api/reference/naics/industries${qs}`, undefined, signal));
  }

  // ---- company rate schedule ----
  async listServiceRates(params?: ListServiceRatesParams, signal?: AbortSignal): Promise<ServiceRate[]> {
    const qs = query({
      disciplineId: params?.disciplineId, serviceTypeId: params?.serviceTypeId, workLocationId: params?.workLocationId,
      payBasis: params?.payBasis, asOf: params?.asOf, includeEnded: params?.includeEnded,
    });
    return serviceRatesFromWire(await this.payrollConfig(`/api/service-rates${qs}`, undefined, signal));
  }

  async createServiceRate(input: CreateServiceRateInput): Promise<ServiceRate> {
    return serviceRateFromWire(await this.payrollConfig('/api/service-rates', post(createServiceRateToWire(input))));
  }

  async endServiceRate(id: string, effectiveTo: string, expectedConcurrencyVersion?: number): Promise<ServiceRate> {
    return serviceRateFromWire(
      await this.payrollConfig(`/api/service-rates/${encodeURIComponent(id)}/end`, post({ effectiveTo, ...(expectedConcurrencyVersion === undefined ? {} : { expectedConcurrencyVersion }) })),
    );
  }

  // ---- employee service rates ----
  async getEmployeeRateCapabilities(signal?: AbortSignal): Promise<EmployeeRateCapabilities> {
    return employeeRateCapabilitiesFromWire(await this.compensation('/api/employee-rate-capabilities', undefined, signal));
  }

  async listEmployeeServiceRates(employeeId: string, params?: ListServiceRatesParams, signal?: AbortSignal): Promise<ServiceRate[]> {
    const qs = query({
      disciplineId: params?.disciplineId, serviceTypeId: params?.serviceTypeId, payBasis: params?.payBasis, asOf: params?.asOf, includeEnded: params?.includeEnded,
    });
    return serviceRatesFromWire(await this.compensation(`/api/employees/${encodeURIComponent(employeeId)}/service-rates${qs}`, undefined, signal));
  }

  async listSupportedPayBases(signal?: AbortSignal): Promise<string[]> {
    return supportedPayBasesFromWire(await this.payrollCalculation('/api/service-lines/supported-pay-bases', undefined, signal));
  }

  async createEmployeeServiceRate(employeeId: string, input: CreateServiceRateInput): Promise<ServiceRate> {
    return serviceRateFromWire(
      await this.compensation(`/api/employees/${encodeURIComponent(employeeId)}/service-rates`, post(createServiceRateToWire(input))),
    );
  }

  async endEmployeeServiceRate(employeeId: string, id: string, effectiveTo: string, expectedConcurrencyVersion?: number): Promise<ServiceRate> {
    return serviceRateFromWire(
      await this.compensation(
        `/api/employees/${encodeURIComponent(employeeId)}/service-rates/${encodeURIComponent(id)}/end`,
        post({ effectiveTo, ...(expectedConcurrencyVersion === undefined ? {} : { expectedConcurrencyVersion }) }),
      ),
    );
  }
}
